export type ConsumerOrderSession = {
  orderId: string;
  friendlyCode?: string;
  saleId: string;
  sellingPointId: string;
  marketWebBaseUrl?: string;
  currency: string;
  amount: number;
  createdAt: string;
  mode: "kiosk" | "table";
  paymentMode?: "cash" | "card_present" | "online_qr" | "tab";
  payUrl?: string;
};

const ORDER_SESSION_PREFIX = "volqan.consumer.order.";

const toText = (value: unknown) => String(value ?? "").trim();

export function buildInvoiceAccessUrl(params: {
  orderId: string;
  saleId: string;
  sellingPointId: string;
  marketWebBaseUrl?: string;
  phone?: string;
}) {
  const baseUrl = toText(params.marketWebBaseUrl) || window.location.origin;
  const url = new URL("/invoice-request", baseUrl);
  url.searchParams.set("orderId", toText(params.orderId));
  url.searchParams.set("saleId", toText(params.saleId));
  url.searchParams.set("sellingPointId", toText(params.sellingPointId));
  if (toText(params.phone)) {
    url.searchParams.set("phone", toText(params.phone));
  }
  return url.toString();
}

export function saveConsumerOrderSession(session: ConsumerOrderSession) {
  if (!toText(session.orderId)) return;
  try {
    sessionStorage.setItem(`${ORDER_SESSION_PREFIX}${session.orderId}`, JSON.stringify(session));
    sessionStorage.setItem(`${ORDER_SESSION_PREFIX}latest`, toText(session.orderId));
  } catch {
    // Ignore storage errors on kiosk browsers with restricted storage.
  }
}

export function readConsumerOrderSession(orderId?: string | null): ConsumerOrderSession | null {
  try {
    const scopedOrderId = toText(orderId) || toText(sessionStorage.getItem(`${ORDER_SESSION_PREFIX}latest`));
    if (!scopedOrderId) return null;
    const raw = sessionStorage.getItem(`${ORDER_SESSION_PREFIX}${scopedOrderId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsumerOrderSession;
    if (!parsed || typeof parsed !== "object") return null;
    if (!toText(parsed.orderId) || !toText(parsed.saleId) || !toText(parsed.sellingPointId)) return null;
    return {
      orderId: toText(parsed.orderId),
      friendlyCode: toText(parsed.friendlyCode) || "",
      saleId: toText(parsed.saleId),
      sellingPointId: toText(parsed.sellingPointId),
      marketWebBaseUrl: toText(parsed.marketWebBaseUrl) || "",
      currency: toText(parsed.currency) || "EUR",
      amount: Number(parsed.amount || 0) || 0,
      createdAt: toText(parsed.createdAt) || new Date().toISOString(),
      mode: parsed.mode === "table" ? "table" : "kiosk",
      paymentMode:
        parsed.paymentMode === "cash" ||
        parsed.paymentMode === "card_present" ||
        parsed.paymentMode === "online_qr" ||
        parsed.paymentMode === "tab"
          ? parsed.paymentMode
          : undefined,
      payUrl: toText(parsed.payUrl) || "",
    };
  } catch {
    return null;
  }
}
